import { useState, useEffect, useCallback, useMemo } from 'react';
import { filterAssessments, assessmentScopeTotal } from '../models/AssessmentsModel';
import { scenarioComplete } from '../models/LibraryModel';

const sameNames = (a, b) => a === b || (a.length === b.length && a.every((d, i) => d.id === b[i].id && d.name === b[i].name));

/**
 * ViewModel for Data Sources > Assessments (PRD-1703). It follows the
 * Assessments model and the Library model beside it, whose scenarios the
 * filter, the cards and Add assessment read.
 */
export const useAssessmentsViewModel = (model) => {
  const [state, setState] = useState(() => model.getState());
  const [libraryState, setLibraryState] = useState(() => model.library.getState());

  useEffect(() => {
    setState(model.getState());
    return model.subscribe(setState);
  }, [model]);

  // Only the scenarios and document names matter here, so a Library upload
  // tick, which changes a document's progress and nothing else, changes nothing.
  useEffect(() => {
    const keep = (prev, next) => (prev.scenarios === next.scenarios && sameNames(prev.documents, next.documents) ? prev : next);
    setLibraryState((prev) => keep(prev, model.library.getState()));
    return model.library.subscribe((next) => setLibraryState((prev) => keep(prev, next)));
  }, [model]);

  const scenarios = useMemo(() => libraryState.scenarios || [], [libraryState.scenarios]);
  const scenarioName = useCallback((id) => scenarios.find((s) => s.id === id)?.name || '-', [scenarios]);
  // What Add assessment can pick: complete (a reference key) and not archived.
  const usableScenarios = useMemo(() => scenarios.filter((s) => scenarioComplete(s) && !s.archived), [scenarios]);
  const narrativeName = useCallback(
    (scenario) => libraryState.documents.find((d) => d.id === scenario.narrativeId)?.name || null,
    [libraryState.documents],
  );

  const { assessments, filters } = state;
  const visibleAssessments = useMemo(
    () => filterAssessments(assessments, filters, scenarioName),
    [assessments, filters, scenarioName],
  );
  const scopeTotal = useMemo(() => assessmentScopeTotal(assessments, filters.visibility), [assessments, filters.visibility]);

  const setFilter = useCallback((key, value) => model.setFilter(key, value), [model]);
  const setView = useCallback((view) => model.setView(view), [model]);
  const setListSort = useCallback((sort) => model.setListSort(sort), [model]);
  const clearSearchAndFilters = useCallback(() => model.clearSearchAndFilters(), [model]);
  const setDraft = useCallback((patch) => model.setDraft(patch), [model]);
  const addFiles = useCallback((files) => model.addFiles(files), [model]);
  const setFileDuration = useCallback((key, sec) => model.setFileDuration(key, sec), [model]);
  const removeFile = useCallback((key) => model.removeFile(key), [model]);
  const addAssessments = useCallback((fields) => model.addAssessments(fields), [model]);
  const retry = useCallback((id) => model.retry(id), [model]);
  const archive = useCallback((id) => model.archive(id), [model]);
  const unarchive = useCallback((id) => model.unarchive(id), [model]);
  const setReturnToAdd = useCallback((value) => model.setReturnToAdd(value), [model]);

  return {
    ...state,
    total: assessments.length,
    scopeTotal,
    visibleAssessments,
    scenarios,
    // The Library's documents, for the names an assessment's scenario binds.
    documents: libraryState.documents,
    usableScenarios,
    scenarioName,
    narrativeName,
    setFilter,
    setView,
    setListSort,
    clearSearchAndFilters,
    setDraft,
    addFiles,
    setFileDuration,
    removeFile,
    addAssessments,
    retry,
    archive,
    unarchive,
    setReturnToAdd,
  };
};

/**
 * Whether the user came to Settings > Assessments from Add assessment, so the
 * scenarios page can offer the way back (handoff story 15). `model` is null
 * while `navy-assessments` is off.
 */
export const useReturnToAddAssessment = (model) => {
  const [returnToAdd, setReturnToAdd] = useState(() => !!model?.getState().returnToAdd);
  useEffect(() => {
    if (!model) return undefined;
    setReturnToAdd(model.getState().returnToAdd);
    return model.subscribe((state) => setReturnToAdd(state.returnToAdd));
  }, [model]);
  const clear = useCallback(() => model?.setReturnToAdd(false), [model]);
  return { returnToAdd: !!model && returnToAdd, clear };
};
